import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

function TableMaster({ token, selectedPropertyId }) {
  const [tables, setTables] = useState([]);
  const [form, setForm] = useState({ table_name: '', capacity: '', zone: '', is_active: true });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const resolvePropertyId = () => String(selectedPropertyId || localStorage.getItem('selectedPropertyId') || '').trim();
  const authHeaders = (extra = {}) => ({ Authorization: `Bearer ${token}`, 'x-property-id': resolvePropertyId(), ...extra });

  useEffect(() => {
    if (!resolvePropertyId()) {
      setTables([]);
      setError('Select a property first from Hotel Config');
      return;
    }
    setError('');
    loadTables();
  }, [token, selectedPropertyId]);

  const loadTables = () => {
    if (!resolvePropertyId()) {
      setTables([]);
      setError('Select a property first from Hotel Config');
      return;
    }
    fetch('/api/table-master', {
      headers: authHeaders()
    })
      .then(res => res.json())
      .then(data => setTables(Array.isArray(data) ? data : []))
      .catch(() => {
        setTables([]);
        setError('Failed to load tables');
      });
  };

  const handleSave = async () => {
    if (!resolvePropertyId()) {
      setError('Select a property first from Hotel Config');
      return;
    }
    if (!form.table_name.trim()) {
      setError('Table name required');
      return;
    }

    try {
      const res = await fetch(editingId ? `/api/table-master/${editingId}` : '/api/table-master', {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Save failed');
        return;
      }
      setError('');
      setForm({ table_name: '', capacity: '', zone: '', is_active: true });
      setEditingId(null);
      loadTables();
    } catch {
      setError('Network error');
    }
  };

  const handleEdit = row => {
    setEditingId(row.id);
    setForm({
      table_name: row.table_name || '',
      capacity: row.capacity == null ? '' : String(row.capacity),
      zone: row.zone || '',
      is_active: row.is_active !== false
    });
    setError('');
    scrollToForm();
  };

  const handleDelete = async id => {
    if (!resolvePropertyId()) {
      setError('Select a property first from Hotel Config');
      return;
    }
    try {
      const res = await fetch(`/api/table-master/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Delete failed');
        return;
      }
      setError('');
      loadTables();
    } catch {
      setError('Network error');
    }
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>Table Master</h2>
      {error && <div className="error-banner">{error}</div>}

      <div className="filter-bar" ref={formRef} style={editHighlightStyle(editHighlight)}>
        <h3>{editingId ? 'Edit Table' : 'Add Table'}</h3>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:'1rem' }}>
          <input
            placeholder="Table Name"
            value={form.table_name}
            onChange={e => setForm({ ...form, table_name: e.target.value })}
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          />
          <input
            type="number"
            min="1"
            placeholder="Capacity (optional)"
            value={form.capacity}
            onChange={e => setForm({ ...form, capacity: e.target.value })}
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          />
          <input
            placeholder="Zone (optional)"
            value={form.zone}
            onChange={e => setForm({ ...form, zone: e.target.value })}
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          />
          <label style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={e => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        </div>
        <div style={{ marginTop:'1rem', display:'flex', gap:'0.75rem' }}>
          <button onClick={handleSave} className="btn-primary" style={{ padding:'0.75rem 1.3rem', border:'none', borderRadius:'6px', cursor:'pointer' }}>
            {editingId ? 'Update' : 'Add'}
          </button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setForm({ table_name: '', capacity: '', zone: '', is_active: true }); }} className="btn-muted" style={{ padding:'0.75rem 1.3rem', border:'none', borderRadius:'6px', cursor:'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3>Tables</h3>
      {tables.length === 0 ? (
        <p>No tables found</p>
      ) : (
        <table style={{ width:'100%', borderCollapse:'collapse', background:'#fff' }}>
          <thead>
            <tr style={{ background:'var(--theme-primary-dark)', color:'#fff' }}>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>ID</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>Table Name</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>Capacity</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>Zone</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>Active</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {tables.map(row => (
              <tr key={row.id} style={{ borderBottom:'1px solid #dcdde1' }}>
                <td style={{ padding:'0.75rem' }}>{row.id}</td>
                <td style={{ padding:'0.75rem' }}>{row.table_name}</td>
                <td style={{ padding:'0.75rem' }}>{row.capacity || '-'}</td>
                <td style={{ padding:'0.75rem' }}>{row.zone || '-'}</td>
                <td style={{ padding:'0.75rem' }}>{row.is_active ? 'Yes' : 'No'}</td>
                <td style={{ padding:'0.75rem', display:'flex', gap:'0.5rem' }}>
                  <button onClick={() => handleEdit(row)} style={{ border:'none', borderRadius:'4px', padding:'0.45rem 0.8rem', background:'#3498db', color:'#fff', cursor:'pointer' }}>Edit</button>
                  <button onClick={() => handleDelete(row.id)} className="btn-danger" style={{ border:'none', borderRadius:'4px', padding:'0.45rem 0.8rem', cursor:'pointer' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default TableMaster;
